<template>
  <Layout>
    <PageHeader :title="$t('Sales Reporting')" :items="breadcrumbItems">
      <template slot="title">
        <div class="d-flex align-items-center">
          <i class="bx bx-line-chart mr-2"></i>
          {{ $t("Sales Reporting") }}
        </div>
      </template>
    </PageHeader>

    <b-row>
      <b-col lg="12">
        <b-card>
          <!-- Filters Section -->
          <div class="d-flex flex-wrap gap-3 mb-4 mt-3">
            <!-- Period Selector -->
            <div style="flex: 1; max-width: 200px">
              <label>{{ $t("Period") }}</label>
              <b-form-select
                v-model="selectedPeriod"
                :options="periodOptions"
                @change="onPeriodChange"
              />
            </div>

            <!-- Granularity -->
            <div style="flex: 0 0 150px">
              <label>{{ $t("Granularity") }}</label>
              <b-form-select
                v-model="filters.granularity"
                :options="granularityOptions"
                @change="loadData"
              />
            </div>

            <!-- Date Range (only for custom) -->
            <div v-if="selectedPeriod === 'custom'" style="flex: 0 0 150px">
              <label>{{ $t("Start Date") }}</label>
              <b-form-input
                v-model="filters.startDate"
                type="date"
                @change="loadData"
              />
            </div>
            <div v-if="selectedPeriod === 'custom'" style="flex: 0 0 150px">
              <label>{{ $t("End Date") }}</label>
              <b-form-input
                v-model="filters.endDate"
                type="date"
                @change="loadData"
              />
            </div>

            <!-- View Type -->
            <div style="flex: 0 0 180px">
              <label>{{ $t("View") }}</label>
              <b-form-select
                v-model="viewType"
                :options="viewTypeOptions"
                @change="loadData"
              />
            </div>

            <!-- Actions -->
            <div class="d-flex align-items-end" style="flex: 0 0 auto">
              <b-button
                variant="primary"
                size="sm"
                @click="toggleFilters"
                class="mr-2"
              >
                <i class="bx bx-filter"></i>
                {{ $t("Filters") }}
              </b-button>
              <b-button
                variant="outline-secondary"
                size="sm"
                @click="loadData"
                :disabled="loading"
              >
                <i class="bx bx-refresh"></i>
              </b-button>
            </div>
          </div>

          <!-- Advanced Filters (Collapsible) -->
          <b-collapse v-model="showAdvancedFilters">
            <b-card bg-variant="light" class="mb-4">
              <!-- First Row removed: Status filter no longer used -->

              <!-- Second Row: Customer, Item Groups, Tags -->
              <div class="d-flex flex-wrap gap-3 mb-3">
                <div style="flex: 1; min-width: 250px">
                  <label>{{ $t("Customers") }}</label>
                  <multiselect
                    v-model="filters.customers"
                    :options="customerOptions"
                    :multiple="true"
                    :close-on-select="false"
                    :clear-on-select="false"
                    :preserve-search="true"
                    placeholder="Select customers"
                    label="company_name"
                    track-by="id"
                    @input="loadData"
                  />
                </div>

                <div style="flex: 1; min-width: 250px">
                  <label>{{ $t("Item Groups") }}</label>
                  <multiselect
                    v-model="filters.itemGroups"
                    :options="itemGroupOptions"
                    :multiple="true"
                    :close-on-select="false"
                    :clear-on-select="false"
                    :preserve-search="true"
                    placeholder="Select item groups"
                    label="description"
                    track-by="id"
                    @input="loadData"
                  />
                </div>

                <div style="flex: 1; min-width: 250px">
                  <label>{{ $t("Tags") }}</label>
                  <multiselect
                    v-model="filters.tags"
                    :options="tagOptions"
                    :multiple="true"
                    :close-on-select="false"
                    :clear-on-select="false"
                    :preserve-search="true"
                    :taggable="true"
                    placeholder="Select tags"
                    @input="loadData"
                  />
                </div>

                <div
                  v-if="filters.tags && filters.tags.length > 1"
                  style="flex: 0 0 150px"
                >
                  <label>{{ $t("Tag Match") }}</label>
                  <b-form-select
                    v-model="filters.tagMode"
                    :options="tagModeOptions"
                    @change="loadData"
                  />
                </div>
              </div>

              <!-- Third Row: Clear Filters -->
              <div class="d-flex">
                <b-button
                  variant="outline-danger"
                  size="sm"
                  @click="clearFilters"
                >
                  <i class="bx bx-x"></i>
                  {{ $t("Clear Filters") }}
                </b-button>
              </div>
            </b-card>
          </b-collapse>

          <!-- Loading State -->
          <div v-if="loading" class="text-center py-5">
            <b-spinner variant="primary" label="Loading..."></b-spinner>
            <p class="mt-2">{{ $t("Loading data...") }}</p>
          </div>

          <!-- Data Display -->
          <div v-else>
            <!-- Summary Cards -->
            <b-row class="mb-4">
              <b-col md="3">
                <div class="card mini-stats-wid">
                  <div class="card-body">
                    <div class="d-flex">
                      <div class="flex-grow-1">
                        <p class="text-muted fw-medium">
                          {{ $t("Total Revenue") }}
                        </p>
                        <h4 class="mb-0">
                          {{ formatCurrency(summaryStats.totalRevenue) }}
                        </h4>
                      </div>
                      <div class="flex-shrink-0 align-self-center">
                        <div
                          class="mini-stat-icon avatar-sm rounded-circle bg-primary"
                        >
                          <span class="avatar-title">
                            <i class="bx bx-euro font-size-24"></i>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </b-col>

              <b-col md="3">
                <div class="card mini-stats-wid">
                  <div class="card-body">
                    <div class="d-flex">
                      <div class="flex-grow-1">
                        <p class="text-muted fw-medium">
                          {{ $t("Total Quantity") }}
                        </p>
                        <h4 class="mb-0">
                          {{ formatNumber(summaryStats.totalQty) }}
                        </h4>
                      </div>
                      <div class="flex-shrink-0 align-self-center">
                        <div
                          class="mini-stat-icon avatar-sm rounded-circle bg-success"
                        >
                          <span class="avatar-title">
                            <i class="bx bx-package font-size-24"></i>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </b-col>

              <b-col md="3">
                <div class="card mini-stats-wid">
                  <div class="card-body">
                    <div class="d-flex">
                      <div class="flex-grow-1">
                        <p class="text-muted fw-medium">
                          {{ $t("Order Lines") }}
                        </p>
                        <h4 class="mb-0">
                          {{ formatNumber(summaryStats.totalLines) }}
                        </h4>
                      </div>
                      <div class="flex-shrink-0 align-self-center">
                        <div
                          class="mini-stat-icon avatar-sm rounded-circle bg-info"
                        >
                          <span class="avatar-title">
                            <i class="bx bx-list-ul font-size-24"></i>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </b-col>

              <b-col md="3">
                <div class="card mini-stats-wid">
                  <div class="card-body">
                    <div class="d-flex">
                      <div class="flex-grow-1">
                        <p class="text-muted fw-medium">
                          {{ $t("Avg per Period") }}
                        </p>
                        <h4 class="mb-0">
                          {{ formatCurrency(summaryStats.avgRevenue) }}
                        </h4>
                      </div>
                      <div class="flex-shrink-0 align-self-center">
                        <div
                          class="mini-stat-icon avatar-sm rounded-circle bg-warning"
                        >
                          <span class="avatar-title">
                            <i class="bx bx-trending-up font-size-24"></i>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </b-col>
              <!-- Open Revenue Card -->
              <b-col md="3">
                <div class="card mini-stats-wid">
                  <div class="card-body">
                    <div class="d-flex">
                      <div class="flex-grow-1">
                        <p class="text-muted fw-medium">
                          {{ $t("Open Revenue") }}
                        </p>
                        <h4 class="mb-0">
                          {{ formatCurrency(summaryStats.totalOpenRevenue) }}
                        </h4>
                      </div>
                      <div class="flex-shrink-0 align-self-center">
                        <div
                          class="mini-stat-icon avatar-sm rounded-circle bg-primary"
                        >
                          <span class="avatar-title">
                            <i class="bx bx-time-five font-size-24"></i>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </b-col>

              <!-- Delivered Not Invoiced Revenue Card -->
              <b-col md="3">
                <div class="card mini-stats-wid">
                  <div class="card-body">
                    <div class="d-flex">
                      <div class="flex-grow-1">
                        <p class="text-muted fw-medium">
                          {{ $t("Delivered Not Invoiced Revenue") }}
                        </p>
                        <h4 class="mb-0">
                          {{
                            formatCurrency(
                              summaryStats.totalDeliveredNotInvoicedRevenue
                            )
                          }}
                        </h4>
                      </div>
                      <div class="flex-shrink-0 align-self-center">
                        <div
                          class="mini-stat-icon avatar-sm rounded-circle bg-warning"
                        >
                          <span class="avatar-title">
                            <i class="bx bx-send font-size-24"></i>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </b-col>
            </b-row>

            <!-- Chart Section -->
            <b-card class="mb-4">
              <h5 class="card-title mb-4">{{ $t("Revenue Trend") }}</h5>
              <apexchart
                type="line"
                height="350"
                :options="chartOptions"
                :series="chartSeries"
              ></apexchart>
            </b-card>

            <!-- Data Table -->
            <b-card>
              <h5 class="card-title mb-4">{{ $t("Detailed Data") }}</h5>

              <!-- Export Button -->
              <div class="mb-3 text-right">
                <b-button
                  variant="outline-primary"
                  size="sm"
                  @click="exportToCSV"
                >
                  <i class="bx bx-download"></i>
                  {{ $t("Export CSV") }}
                </b-button>
              </div>

              <b-table
                :items="tableData"
                :fields="tableFields"
                :per-page="perPage"
                :current-page="currentPage"
                responsive
                striped
                hover
                :sort-by.sync="sortBy"
                :sort-desc.sync="sortDesc"
              >
                <!-- Period formatting -->
                <template #cell(period_start)="data">
                  {{ formatDate(data.value) }}
                </template>

                <!-- Customer formatting -->
                <template #cell(company_name)="data">
                  {{ data.value }}
                </template>

                <!-- Item Group formatting -->
                <template #cell(item_group_name)="data">
                  {{ data.value }}
                </template>

                <!-- Revenue formatting -->
                <template #cell(revenue)="data">
                  {{ formatCurrency(data.value) }}
                </template>

                <!-- Quantity formatting -->
                <template #cell(qty)="data">
                  {{ formatNumber(data.value) }}
                </template>

                <!-- Status badge -->
                <template #cell(status)="data">
                  <b-badge :variant="getStatusVariant(data.value)">
                    {{ getStatusLabel(data.value) }}
                  </b-badge>
                </template>

                <!-- Delta columns with trend indicators -->
                <template #cell(revenue_vs_prev)="data">
                  <span
                    v-if="data.value !== null"
                    :class="getDeltaClass(data.value)"
                  >
                    <i :class="getDeltaIcon(data.value)"></i>
                    {{ formatCurrency(Math.abs(data.value)) }}
                  </span>
                  <span v-else>-</span>
                </template>

                <template #cell(revenue_vs_yoy)="data">
                  <span
                    v-if="data.value !== null"
                    :class="getDeltaClass(data.value)"
                  >
                    <i :class="getDeltaIcon(data.value)"></i>
                    {{ formatCurrency(Math.abs(data.value)) }}
                  </span>
                  <span v-else>-</span>
                </template>
              </b-table>

              <!-- Pagination -->
              <b-pagination
                v-if="tableData.length > perPage"
                v-model="currentPage"
                :total-rows="tableData.length"
                :per-page="perPage"
                align="center"
                class="mt-3"
              ></b-pagination>
            </b-card>
          </div>
        </b-card>
      </b-col>
    </b-row>
  </Layout>
</template>

<script>
import Layout from "@/components/layout/app-layout";
import PageHeader from "@/components/layout/page-header";
import Multiselect from "vue-multiselect";
import api from "@/helpers/api";

export default {
  name: "StatsSalesReporting",
  components: {
    Layout,
    PageHeader,
    Multiselect,
  },
  data() {
    return {
      loading: false,
      showAdvancedFilters: false,
      viewType: "aggregated",
      selectedPeriod: "this_year",

      filters: {
        startDate: this.getDefaultStartDate(),
        endDate: this.getDefaultEndDate(),
        granularity: "month",
        customers: [],
        itemGroups: [],
        tags: [],
        tagMode: "any",
      },

      // Options for dropdowns
      periodOptions: [
        { value: "today", text: this.$t("Today") },
        { value: "yesterday", text: this.$t("Yesterday") },
        { value: "this_week", text: this.$t("This Week") },
        { value: "last_week", text: this.$t("Last Week") },
        { value: "this_month", text: this.$t("This Month") },
        { value: "last_month", text: this.$t("Last Month") },
        { value: "last_3_months", text: this.$t("Last 3 Months") },
        { value: "last_6_months", text: this.$t("Last 6 Months") },
        { value: "this_quarter", text: this.$t("This Quarter") },
        { value: "last_quarter", text: this.$t("Last Quarter") },
        { value: "this_year", text: this.$t("This Year") },
        { value: "last_year", text: this.$t("Last Year") },
        { value: "custom", text: this.$t("Custom Range") },
      ],
      granularityOptions: [
        { value: "week", text: this.$t("Weekly") },
        { value: "month", text: this.$t("Monthly") },
        { value: "quarter", text: this.$t("Quarterly") },
        { value: "year", text: this.$t("Yearly") },
      ],
      viewTypeOptions: [
        { value: "aggregated", text: this.$t("Aggregated View") },
        { value: "custom", text: this.$t("Custom Report") },
      ],
      tagModeOptions: [
        { value: "any", text: this.$t("Any Tag") },
        { value: "all", text: this.$t("All Tags") },
      ],

      customerOptions: [],
      itemGroupOptions: [],
      tagOptions: [],

      // Data
      reportData: [],
      previousReportData: [],
      summaryStats: {
        totalRevenue: 0,
        totalQty: 0,
        totalLines: 0,
        avgRevenue: 0,
        totalOpenRevenue: 0,
        totalDeliveredNotInvoicedRevenue: 0,
      },

      // Table
      tableFields: [],
      perPage: 25,
      currentPage: 1,
      sortBy: "period_start",
      sortDesc: false,

      // Charts
      chartOptions: {},
      chartSeries: [],

      breadcrumbItems: [
        {
          text: this.$t("Statistics"),
        },
        {
          text: this.$t("Sales Reporting"),
          active: true,
        },
      ],
    };
  },
  computed: {
    tableData() {
      return this.reportData
        .filter((item) => {
          // Filter out rows where all values are zero
          const qty = parseFloat(item.qty || 0);
          const revenue = parseFloat(item.revenue || 0);
          const lineCount = parseInt(item.line_count || 0);

          return qty !== 0 || revenue !== 0 || lineCount !== 0;
        })
        .map((item) => {
          const formatted = { ...item };

          // Add company name from account relationship
          if (item.account && item.account.company_name) {
            formatted.company_name = item.account.company_name;
          } else {
            formatted.company_name = this.getCustomerName(
              item.customer_account_id
            );
          }

          // Add item group name from relationship
          if (item.item_group) {
            formatted.item_group_name =
              item.item_group.description || item.item_group.code;
          } else {
            formatted.item_group_name = this.getItemGroupName(
              item.top_group_id
            );
          }

          // Add delta columns based on granularity
          if (this.viewType === "aggregated") {
            switch (this.filters.granularity) {
              case "month":
                formatted.revenue_vs_prev = item.revenue_vs_prev_month;
                formatted.revenue_vs_yoy = item.revenue_vs_same_month_last_year;
                break;
              case "quarter":
                formatted.revenue_vs_prev = item.revenue_vs_prev_quarter;
                formatted.revenue_vs_yoy =
                  item.revenue_vs_same_quarter_last_year;
                break;
              case "week":
                formatted.revenue_vs_prev = item.revenue_vs_prev_week;
                formatted.revenue_vs_yoy = item.revenue_vs_same_week_last_year;
                break;
              case "year":
                formatted.revenue_vs_prev = item.revenue_vs_prev_year;
                formatted.revenue_vs_yoy = null;
                break;
            }
          }

          return formatted;
        });
    },
  },
  async mounted() {
    await this.loadLookupData();
    await this.loadData();
  },
  methods: {
    async loadLookupData() {
      try {
        // Load customers
        this.customerOptions = await api.salesReporting.getCustomers();

        // Load item groups
        this.itemGroupOptions = await api.salesReporting.getTopItemGroups();

        // Load tags
        this.tagOptions = await api.salesReporting.getUniqueTags();
      } catch (error) {
        console.error("Error loading lookup data:", error);
        this.$bvToast.toast("Failed to load filter options", {
          title: "Error",
          variant: "danger",
        });
      }
    },

    async loadData() {
      this.loading = true;
      try {
        if (this.viewType === "aggregated") {
          await this.loadAggregatedData();
        } else {
          await this.loadCustomReport();
        }

        this.calculateSummaryStats();
        this.prepareCharts();
        this.prepareTableFields();
      } catch (error) {
        console.error("Error loading data:", error);
        this.$bvToast.toast("Failed to load reporting data", {
          title: "Error",
          variant: "danger",
        });
      } finally {
        this.loading = false;
      }
    },

    async loadAggregatedData() {
      // If tags are selected, switch to custom report (aggregated views don't support tag filtering)
      if (this.filters.tags && this.filters.tags.length > 0) {
        await this.loadCustomReport();
        return;
      }

      const where = {
        period_start: {
          _gte: this.filters.startDate,
          _lte: this.filters.endDate,
        },
      };

      // Status filter removed; include all statuses in dataset

      if (this.filters.customers.length > 0) {
        where.customer_account_id = {
          _in: this.filters.customers.map((c) => c.id),
        };
      }

      if (this.filters.itemGroups.length > 0) {
        where.top_group_id = {
          _in: this.filters.itemGroups.map((g) => g.id),
        };
      }

      const filters = {
        where,
        order_by: { period_start: "asc" },
      };

      switch (this.filters.granularity) {
        case "week":
          this.reportData = await api.salesReporting.getWeeklyAggregation(
            filters
          );
          break;
        case "month":
          this.reportData = await api.salesReporting.getMonthlyAggregation(
            filters
          );
          break;
        case "quarter":
          this.reportData = await api.salesReporting.getQuarterlyAggregation(
            filters
          );
          break;
        case "year":
          this.reportData = await api.salesReporting.getYearlyAggregation(
            filters
          );
          break;
      }

      // Load previous period dataset for comparison overlay
      const { prevStartDate, prevEndDate } = this.getPreviousRange(
        this.filters.startDate,
        this.filters.endDate,
        this.filters.granularity
      );
      const prevWhere = {
        ...where,
        period_start: { _gte: prevStartDate, _lte: prevEndDate },
      };
      const prevFilters = {
        where: prevWhere,
        order_by: { period_start: "asc" },
      };

      switch (this.filters.granularity) {
        case "week":
          this.previousReportData = await api.salesReporting.getWeeklyAggregation(
            prevFilters
          );
          break;
        case "month":
          this.previousReportData = await api.salesReporting.getMonthlyAggregation(
            prevFilters
          );
          break;
        case "quarter":
          this.previousReportData = await api.salesReporting.getQuarterlyAggregation(
            prevFilters
          );
          break;
        case "year":
          this.previousReportData = await api.salesReporting.getYearlyAggregation(
            prevFilters
          );
          break;
      }
    },

    async loadCustomReport() {
      const params = {
        p_start_date: this.filters.startDate,
        p_end_date: this.filters.endDate,
        p_customer_ids: null,
        p_top_group_ids: null,
        p_statuses: null,
        p_tags: null,
        p_tag_mode: this.filters.tagMode || "any",
      };

      // Status filter removed; do not constrain by statuses

      if (this.filters.customers.length > 0) {
        // Format as PostgreSQL array: {uuid1,uuid2}
        params.p_customer_ids = `{${this.filters.customers
          .map((c) => c.id)
          .join(",")}}`;
      }

      if (this.filters.itemGroups.length > 0) {
        // Format as PostgreSQL array: {uuid1,uuid2}
        params.p_top_group_ids = `{${this.filters.itemGroups
          .map((g) => g.id)
          .join(",")}}`;
      }

      if (this.filters.tags.length > 0) {
        // Format as PostgreSQL array: {tag1,tag2}
        params.p_tags = `{${this.filters.tags.join(",")}}`;
      }

      const rawFacts = await api.salesReporting.getMetricsReport(params);

      // Aggregate the raw facts by period, customer, group, and status
      this.reportData = this.aggregateFacts(rawFacts, this.filters.granularity);

      // Also load previous period dataset for comparison
      const { prevStartDate, prevEndDate } = this.getPreviousRange(
        this.filters.startDate,
        this.filters.endDate,
        this.filters.granularity
      );
      const paramsPrev = {
        ...params,
        p_start_date: prevStartDate,
        p_end_date: prevEndDate,
      };
      const rawPrevFacts = await api.salesReporting.getMetricsReport(
        paramsPrev
      );
      this.previousReportData = this.aggregateFacts(
        rawPrevFacts,
        this.filters.granularity
      );
    },

    aggregateFacts(facts, granularity) {
      const aggregated = {};

      facts.forEach((fact) => {
        // Truncate date based on granularity
        const date = new Date(fact.status_date);
        let periodKey;

        switch (granularity) {
          case "week": {
            // Get start of week
            const dayOfWeek = date.getDay();
            const startOfWeek = new Date(date);
            startOfWeek.setDate(date.getDate() - dayOfWeek);
            periodKey = startOfWeek.toISOString().split("T")[0];
            break;
          }
          case "month":
            periodKey = `${date.getFullYear()}-${String(
              date.getMonth() + 1
            ).padStart(2, "0")}-01`;
            break;
          case "quarter": {
            const quarter = Math.floor(date.getMonth() / 3);
            periodKey = `${date.getFullYear()}-${String(
              quarter * 3 + 1
            ).padStart(2, "0")}-01`;
            break;
          }
          case "year":
            periodKey = `${date.getFullYear()}-01-01`;
            break;
          default:
            periodKey = fact.status_date;
        }

        const key = `${periodKey}_${fact.customer_account_id}_${fact.top_group_id}_${fact.status}`;

        if (!aggregated[key]) {
          aggregated[key] = {
            period_start: periodKey,
            customer_account_id: fact.customer_account_id,
            top_group_id: fact.top_group_id,
            status: fact.status,
            qty: 0,
            revenue: 0,
            line_count: 0,
          };
        }

        aggregated[key].qty += parseFloat(fact.qty || 0);
        aggregated[key].revenue += parseFloat(fact.revenue || 0);
        aggregated[key].line_count += 1;
      });

      return Object.values(aggregated);
    },

    calculateSummaryStats() {
      this.summaryStats = {
        totalRevenue: 0,
        totalQty: 0,
        totalLines: 0,
        avgRevenue: 0,
        totalOpenRevenue: 0,
        totalDeliveredNotInvoicedRevenue: 0,
      };

      // Sum invoiced revenue only for totalRevenue; keep qty/lines overall
      const periodToInvoiced = {};
      this.reportData.forEach((item) => {
        const revenue = parseFloat(item.revenue || 0);
        this.summaryStats.totalQty += parseFloat(item.qty || 0);
        this.summaryStats.totalLines += parseInt(item.line_count || 0);

        if (item.status === "invoiced") {
          this.summaryStats.totalRevenue += revenue;
          if (!periodToInvoiced[item.period_start])
            periodToInvoiced[item.period_start] = 0;
          periodToInvoiced[item.period_start] += revenue;
        } else if (item.status === "open") {
          this.summaryStats.totalOpenRevenue += revenue;
        } else if (item.status === "delivered_not_invoiced") {
          this.summaryStats.totalDeliveredNotInvoicedRevenue += revenue;
        }
      });

      const periodCount = Object.keys(periodToInvoiced).length;
      this.summaryStats.avgRevenue =
        periodCount > 0 ? this.summaryStats.totalRevenue / periodCount : 0;
    },

    prepareCharts() {
      this.prepareRevenueChart();
    },

    prepareRevenueChart() {
      // Group by period and status
      const periodMap = {};

      this.reportData.forEach((item) => {
        const period = item.period_start;
        if (!periodMap[period]) {
          periodMap[period] = {};
        }
        if (!periodMap[period][item.status]) {
          periodMap[period][item.status] = 0;
        }
        periodMap[period][item.status] += parseFloat(item.revenue || 0);
      });

      const periods = Object.keys(periodMap).sort();
      // Only chart 'invoiced' status
      const statuses = ["invoiced"];

      // Build previous-period map using separately loaded dataset
      const prevPeriodMap = {};
      this.previousReportData.forEach((item) => {
        const period = item.period_start;
        if (!prevPeriodMap[period]) {
          prevPeriodMap[period] = {};
        }
        if (!prevPeriodMap[period][item.status]) {
          prevPeriodMap[period][item.status] = 0;
        }
        prevPeriodMap[period][item.status] += parseFloat(item.revenue || 0);
      });
      // Build current period series (bars for invoiced)
      const currentSeries = statuses.map((status) => ({
        name: this.getStatusLabel(status),
        type: "column",
        data: periods.map((period) => periodMap[period][status] || 0),
      }));

      // Build previous period series aligned to current periods with fallback
      const previousSeries = statuses.map((status) => {
        const data = periods.map((period, idx) => {
          const prevKey = this.getPreviousPeriodKey(period);
          const valFromPrev =
            prevPeriodMap[prevKey] && prevPeriodMap[prevKey][status]
              ? prevPeriodMap[prevKey][status]
              : null;
          if (valFromPrev !== null) return valFromPrev;
          // Fallback: shift current map by one period within current range
          const prevPeriodInCurrent = periods[idx - 1];
          if (
            prevPeriodInCurrent &&
            periodMap[prevPeriodInCurrent] &&
            periodMap[prevPeriodInCurrent][status] !== undefined
          ) {
            return periodMap[prevPeriodInCurrent][status];
          }
          return 0;
        });
        return {
          name: `${this.getStatusLabel(status)} (Prev)`,
          type: "column",
          data,
        };
      });

      // Trend line based on moving average of current invoiced values
      const trendData = this.computeMovingAverage(
        currentSeries[0] ? currentSeries[0].data : [],
        this.getTrendWindow()
      );
      const trendSeries = {
        name: this.$t("Trend"),
        type: "line",
        data: trendData,
      };

      // Determine trend direction for coloring: green if rising, red if falling
      const trendFirst = trendData.length > 0 ? trendData[0] : 0;
      const trendLast =
        trendData.length > 0 ? trendData[trendData.length - 1] : 0;
      const trendColor = trendLast - trendFirst >= 0 ? "#28a745" : "#ff4d4f";

      // Put previous bars first, then current bars, then trend line
      const combinedSeries = [...previousSeries, ...currentSeries, trendSeries];
      this.chartSeries = combinedSeries;

      // Indices for series
      const trendIndex = combinedSeries.length - 1;
      // Create dash array: dashed only for trend line
      const dashArray = combinedSeries.map((_, idx) =>
        idx === trendIndex ? 6 : 0
      );
      // Per-series stroke widths: thinner dashed trend line, bars zero
      const strokeWidths = combinedSeries.map((s, idx) =>
        s.type === "line" ? (idx === trendIndex ? 2 : 2) : 0
      );
      // Per-series marker sizes: small markers on trend only
      const markerSizes = combinedSeries.map((_, idx) =>
        idx === trendIndex ? 2 : 0
      );
      // Tooltip: disable on trend series so it doesn't show duplicate values
      const enabledTooltipSeries = combinedSeries
        .map((_, idx) => idx)
        .filter((idx) => idx !== trendIndex);

      // Legend colors that mirror series (accounting for opacity)
      const prevLegendColor = "rgba(43, 142, 240, 0.35)";
      const currentLegendColor = "rgba(43, 142, 240, 0.85)";

      this.chartOptions = {
        chart: {
          type: "line",
          toolbar: { show: true },
        },
        xaxis: {
          categories: periods.map((p) => this.formatDate(p)),
        },
        yaxis: {
          labels: {
            formatter: (val) => this.formatCurrency(val),
          },
        },
        stroke: {
          curve: "smooth",
          width: strokeWidths,
          dashArray,
        },
        markers: {
          size: markerSizes,
        },
        plotOptions: {
          bar: {
            columnWidth: "40%",
            borderRadius: 3,
          },
        },
        // Colors: prev bars (same as current, more transparent), current bars, dynamic trend color
        colors: ["#2b8ef0", "#2b8ef0", trendColor],
        // Make previous bars more transparent
        fill: {
          opacity: [0.35, 0.85, 1],
        },
        legend: {
          markers: {
            fillColors: [prevLegendColor, currentLegendColor, trendColor],
          },
        },
        tooltip: {
          enabledOnSeries: enabledTooltipSeries,
          y: {
            formatter: (val) => this.formatCurrency(val),
          },
        },
      };
    },

    getPreviousPeriodKey(periodStart) {
      if (!periodStart) return "";
      const date = new Date(periodStart);
      switch (this.filters.granularity) {
        case "week": {
          const prev = new Date(date);
          prev.setDate(prev.getDate() - 7);
          // Align to start of week (Sunday-based, consistent with other logic)
          const dayOfWeek = prev.getDay();
          const startOfWeek = new Date(prev);
          startOfWeek.setDate(prev.getDate() - dayOfWeek);
          return startOfWeek.toISOString().split("T")[0];
        }
        case "month": {
          const prev = new Date(date.getFullYear(), date.getMonth() - 1, 1);
          return prev.toISOString().split("T")[0];
        }
        case "quarter": {
          const prev = new Date(date.getFullYear(), date.getMonth() - 3, 1);
          const qStartMonth = Math.floor(prev.getMonth() / 3) * 3;
          const qStart = new Date(prev.getFullYear(), qStartMonth, 1);
          return qStart.toISOString().split("T")[0];
        }
        case "year": {
          const prev = new Date(date.getFullYear() - 1, 0, 1);
          return prev.toISOString().split("T")[0];
        }
        default:
          return periodStart;
      }
    },

    getPreviousRange(startDateStr, endDateStr, granularity) {
      const start = new Date(startDateStr);
      const end = new Date(endDateStr);
      switch (granularity) {
        case "week": {
          const prevStart = new Date(start);
          prevStart.setDate(prevStart.getDate() - 7);
          const prevEnd = new Date(end);
          prevEnd.setDate(prevEnd.getDate() - 7);
          return {
            prevStartDate: prevStart.toISOString().split("T")[0],
            prevEndDate: prevEnd.toISOString().split("T")[0],
          };
        }
        case "month": {
          const prevStart = new Date(
            start.getFullYear(),
            start.getMonth() - 1,
            1
          );
          const prevEnd = new Date(end.getFullYear(), end.getMonth(), 0);
          return {
            prevStartDate: prevStart.toISOString().split("T")[0],
            prevEndDate: prevEnd.toISOString().split("T")[0],
          };
        }
        case "quarter": {
          const prevStart = new Date(
            start.getFullYear(),
            start.getMonth() - 3,
            1
          );
          const prevEnd = new Date(end.getFullYear(), end.getMonth() - 2, 0);
          return {
            prevStartDate: prevStart.toISOString().split("T")[0],
            prevEndDate: prevEnd.toISOString().split("T")[0],
          };
        }
        case "year": {
          const prevStart = new Date(start.getFullYear() - 1, 0, 1);
          const prevEnd = new Date(end.getFullYear() - 1, 11, 31);
          return {
            prevStartDate: prevStart.toISOString().split("T")[0],
            prevEndDate: prevEnd.toISOString().split("T")[0],
          };
        }
        default: {
          const prevStart = new Date(start);
          const prevEnd = new Date(end);
          prevStart.setDate(prevStart.getDate() - 7);
          prevEnd.setDate(prevEnd.getDate() - 7);
          return {
            prevStartDate: prevStart.toISOString().split("T")[0],
            prevEndDate: prevEnd.toISOString().split("T")[0],
          };
        }
      }
    },

    getTrendWindow() {
      switch (this.filters.granularity) {
        case "week":
          return 4; // 4-week MA
        case "quarter":
          return 2; // 2-quarter MA
        case "year":
          return 2; // 2-year MA
        case "month":
        default:
          return 3; // 3-month MA
      }
    },

    computeMovingAverage(values, windowSize) {
      if (!values || values.length === 0 || windowSize <= 1)
        return values || [];
      const result = [];
      let windowSum = 0;
      const queue = [];
      for (let i = 0; i < values.length; i++) {
        const v = Number(values[i] || 0);
        queue.push(v);
        windowSum += v;
        if (queue.length > windowSize) {
          windowSum -= queue.shift();
        }
        const denom = Math.min(queue.length, windowSize);
        result.push(denom > 0 ? windowSum / denom : 0);
      }
      return result;
    },

    prepareTableFields() {
      this.tableFields = [
        { key: "period_start", label: this.$t("Period"), sortable: true },
        { key: "company_name", label: this.$t("Customer"), sortable: true },
        {
          key: "item_group_name",
          label: this.$t("Item Group"),
          sortable: true,
        },
        { key: "status", label: this.$t("Status"), sortable: true },
        { key: "revenue", label: this.$t("Revenue"), sortable: true },
        { key: "qty", label: this.$t("Quantity"), sortable: true },
      ];

      if (this.viewType === "aggregated") {
        this.tableFields.push(
          {
            key: "revenue_vs_prev",
            label: this.$t("vs Previous"),
            sortable: true,
          },
          {
            key: "revenue_vs_yoy",
            label: this.$t("vs Last Year"),
            sortable: true,
          }
        );
      }
    },

    toggleFilters() {
      this.showAdvancedFilters = !this.showAdvancedFilters;
    },

    clearFilters() {
      this.filters.customers = [];
      this.filters.itemGroups = [];
      this.filters.tags = [];
      this.loadData();
    },

    onPeriodChange() {
      const dates = this.calculateDateRange(this.selectedPeriod);
      this.filters.startDate = dates.startDate;
      this.filters.endDate = dates.endDate;
      this.loadData();
    },

    calculateDateRange(period) {
      const today = new Date();
      let startDate = new Date();
      let endDate = new Date();

      switch (period) {
        case "today":
          startDate = new Date(today);
          endDate = new Date(today);
          break;

        case "yesterday":
          startDate = new Date(today);
          startDate.setDate(today.getDate() - 1);
          endDate = new Date(startDate);
          break;

        case "this_week":
          startDate = new Date(today);
          startDate.setDate(today.getDate() - today.getDay());
          endDate = new Date(today);
          break;

        case "last_week":
          startDate = new Date(today);
          startDate.setDate(today.getDate() - today.getDay() - 7);
          endDate = new Date(startDate);
          endDate.setDate(startDate.getDate() + 6);
          break;

        case "this_month":
          startDate = new Date(today.getFullYear(), today.getMonth(), 1);
          endDate = new Date(today);
          break;

        case "last_month":
          startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
          endDate = new Date(today.getFullYear(), today.getMonth(), 0);
          break;

        case "last_3_months":
          startDate = new Date(today);
          startDate.setMonth(today.getMonth() - 3);
          endDate = new Date(today);
          break;

        case "last_6_months":
          startDate = new Date(today);
          startDate.setMonth(today.getMonth() - 6);
          endDate = new Date(today);
          break;

        case "this_quarter": {
          const currentQuarter = Math.floor(today.getMonth() / 3);
          startDate = new Date(today.getFullYear(), currentQuarter * 3, 1);
          endDate = new Date(today);
          break;
        }

        case "last_quarter": {
          const lastQuarter = Math.floor(today.getMonth() / 3) - 1;
          const lastQuarterYear =
            lastQuarter < 0 ? today.getFullYear() - 1 : today.getFullYear();
          const lastQuarterMonth = lastQuarter < 0 ? 9 : lastQuarter * 3;
          startDate = new Date(lastQuarterYear, lastQuarterMonth, 1);
          endDate = new Date(lastQuarterYear, lastQuarterMonth + 3, 0);
          break;
        }

        case "this_year":
          startDate = new Date(today.getFullYear(), 0, 1);
          endDate = new Date(today);
          break;

        case "last_year":
          startDate = new Date(today.getFullYear() - 1, 0, 1);
          endDate = new Date(today.getFullYear() - 1, 11, 31);
          break;

        case "custom":
          // Keep existing dates
          return {
            startDate: this.filters.startDate,
            endDate: this.filters.endDate,
          };

        default:
          startDate = new Date(today);
          startDate.setMonth(today.getMonth() - 6);
          endDate = new Date(today);
      }

      return {
        startDate: startDate.toISOString().split("T")[0],
        endDate: endDate.toISOString().split("T")[0],
      };
    },

    getDefaultStartDate() {
      const today = new Date();
      const startOfYear = new Date(today.getFullYear(), 0, 1);
      return startOfYear.toISOString().split("T")[0];
    },

    getDefaultEndDate() {
      return new Date().toISOString().split("T")[0];
    },

    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);

      // Format based on granularity to show appropriate detail
      switch (this.filters.granularity) {
        case "year":
          return date.getFullYear().toString();
        case "quarter": {
          const quarter = Math.floor(date.getMonth() / 3) + 1;
          return `Q${quarter} ${date.getFullYear()}`;
        }
        case "month":
          return date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
          });
        case "week": {
          // Get ISO week number
          const weekNumber = this.getWeekNumber(date);
          const monthYear = date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
          });
          return `N° ${weekNumber} - ${monthYear}`;
        }
        default:
          return date.toLocaleDateString();
      }
    },

    getWeekNumber(date) {
      // Get ISO week number
      const d = new Date(
        Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
      );
      const dayNum = d.getUTCDay() || 7;
      d.setUTCDate(d.getUTCDate() + 4 - dayNum);
      const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
      return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
    },

    formatCurrency(value) {
      if (value === null || value === undefined) return "€0.00";
      return new Intl.NumberFormat("nl-NL", {
        style: "currency",
        currency: "EUR",
      }).format(value);
    },

    formatNumber(value) {
      if (value === null || value === undefined) return "0";
      return new Intl.NumberFormat("nl-NL").format(value);
    },

    getStatusVariant(status) {
      const variants = {
        open: "primary",
        delivered_not_invoiced: "warning",
        invoiced: "success",
      };
      return variants[status] || "secondary";
    },

    getStatusLabel(status) {
      const labels = {
        open: this.$t("Open"),
        delivered_not_invoiced: this.$t("Delivered Not Invoiced"),
        invoiced: this.$t("Invoiced"),
      };
      return labels[status] || status;
    },

    getCustomerName(customerId) {
      if (!customerId) return "-";
      const customer = this.customerOptions.find((c) => c.id === customerId);
      return customer ? customer.company_name : customerId.substring(0, 8);
    },

    getItemGroupName(groupId) {
      if (!groupId) return "-";
      const group = this.itemGroupOptions.find((g) => g.id === groupId);
      return group ? group.description || group.code : groupId.substring(0, 8);
    },

    getDeltaClass(value) {
      if (value > 0) return "text-success";
      if (value < 0) return "text-danger";
      return "text-muted";
    },

    getDeltaIcon(value) {
      if (value > 0) return "bx bx-trending-up";
      if (value < 0) return "bx bx-trending-down";
      return "bx bx-minus";
    },

    exportToCSV() {
      const delimiter = ";";
      const serialize = (value) => `"${String(value).replace(/"/g, '""')}"`;
      const formatNumber = (value) => {
        if (value === null || value === undefined || value === "") return "";
        const num = Number(value);
        if (Number.isFinite(num)) {
          return (Math.round(num * 100) / 100).toFixed(2).replace(".", ",");
        }
        return String(value);
      };

      const headers = this.tableFields.map((f) => serialize(f.label));
      const rows = this.tableData.map((item) =>
        this.tableFields.map((f) => serialize(formatNumber(item[f.key])))
      );

      let csv = headers.join(delimiter) + "\n";
      rows.forEach((row) => {
        csv += row.join(delimiter) + "\n";
      });

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `sales-report-${this.filters.startDate}-${this.filters.endDate}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    },
  },
};
</script>

<style scoped>
.mini-stats-wid .card-body {
  padding: 1.25rem;
}

.mini-stat-icon {
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
